import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Download } from 'lucide-react';
import { isIOS, isStandalone, isAndroid, isSafari } from '../utils/platform';

const STORAGE_KEY_DISMISSED = 'installPromptDismissed';
const STORAGE_KEY_LAST_DISMISSED = 'installPromptLastDismissed';
const DISMISS_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours


const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.airchips.poker';

// Custom Apple Icons for instructions
const AppleShareIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const AppleAddIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const PlayStoreIcon = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 28.99 31.99" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M13.54 15.28.12 29.34a3.66 3.66 0 0 0 5.33 2.16l15.1-8.6Z" fill="#ea4335" />
    <path d="m27.11 12.89-6.53-3.74-7.35 6.45 7.38 7.28 6.48-3.7a3.54 3.54 0 0 0 1.5-4.79 3.62 3.62 0 0 0-1.5-1.5z" fill="#fbbc04" />
    <path d="M.12 2.66a3.57 3.57 0 0 0-.12.92v24.84a3.57 3.57 0 0 0 .12.92L14 15.64Z" fill="#4285f4" />
    <path d="m13.64 16 6.94-6.85L5.5.51A3.73 3.73 0 0 0 3.63 0 3.64 3.64 0 0 0 .12 2.65Z" fill="#34a853" />
  </svg>
);

export const InstallPrompt = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [promptType, setPromptType] = useState<'ios-safari' | 'ios-other' | 'android' | null>(null);

  useEffect(() => {
    const handleTrigger = () => {
      if (isStandalone()) return;

      if (isIOS()) {
        setPromptType(isSafari() ? 'ios-safari' : 'ios-other');
        setIsVisible(true);
      } else if (isAndroid()) {
        setPromptType('android');
        setIsVisible(true);
      }
    };

    window.addEventListener('trigger-install-prompt', handleTrigger);
    return () => {
      window.removeEventListener('trigger-install-prompt', handleTrigger);
    };
  }, []);

  useEffect(() => {
    // 1. Check if already standalone
    if (isStandalone()) return;

    // 2. Check if permanently dismissed
    if (localStorage.getItem(STORAGE_KEY_DISMISSED) === 'true') return;

    // 3. Check if recently dismissed (24h)
    const lastDismissed = localStorage.getItem(STORAGE_KEY_LAST_DISMISSED);
    if (lastDismissed && Date.now() - parseInt(lastDismissed, 10) < DISMISS_TIMEOUT) return;

    // 4. Check if there is at least one game in history
    const hasHistory = () => {
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith('poker_sessions_')) {
            const sessions = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(sessions) && sessions.length > 0) return true;
          }
        }
      } catch (e) {
        return false;
      }
      return false;
    };

    if (!hasHistory()) return;

    // 5. Determine platform
    const timer = setTimeout(() => {
      if (isIOS()) {
        setPromptType(isSafari() ? 'ios-safari' : 'ios-other');
        setIsVisible(true);
      } else if (isAndroid()) {
        setPromptType('android');
        setIsVisible(true);
      }
    }, 2000); // Delay to not overwhelm the user immediately

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = (permanently = false) => {
    if (permanently) {
      localStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
    } else {
      localStorage.setItem(STORAGE_KEY_LAST_DISMISSED, Date.now().toString());
    }
    setIsVisible(false);
  };

  if (!promptType) return null;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-x-0 bottom-0 z-[10000] p-4 pointer-events-none sm:p-6 lg:p-8">
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            className="max-w-md mx-auto w-full bg-zinc-900 border border-zinc-800 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] pointer-events-auto overflow-hidden relative"
          >
            {/* Background Accent */}
            <div className={`absolute top-0 right-0 w-32 h-32 blur-3xl opacity-20 pointer-events-none ${
              promptType === 'android' ? 'bg-zinc-500' : 'bg-rose-500'
            }`} />

            <div className="p-6 relative z-10">
              <div className="flex justify-between items-start mb-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
                  promptType === 'android' ? 'bg-zinc-800/50 text-white border-zinc-700/50' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                }`}>
                  {promptType === 'android' ? <PlayStoreIcon className="w-6 h-6" /> : <Download size={24} />}
                </div>
                <button
                  onClick={() => handleDismiss(false)}
                  className="p-2 text-zinc-500 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {promptType === 'android' && (
                <>
                  <h3 className="text-xl font-bold text-white mb-2">Get the App</h3>
                  <p className="text-sm text-zinc-400 mb-6">
                    Get the full experience on the Google Play Store for better performance and native features.
                  </p>
                  <div className="flex flex-col gap-3">
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-white hover:bg-zinc-200 text-black font-bold py-3.5 rounded-xl flex items-center justify-center gap-2.5 transition-all active:scale-95 text-sm"
                    >
                      <PlayStoreIcon className="w-5 h-5" />
                      Get it on Google Play
                    </a>
                  </div>
                </>
              )}

              {promptType === 'ios-safari' && (
                <>
                  <h3 className="text-xl font-bold text-white mb-2">Install AirChips</h3>
                  <p className="text-sm text-zinc-400 mb-5">
                    Install this app on your iPhone for a native full-screen experience.
                  </p>
                  <div className="flex flex-col gap-2.5 mb-2">
                    <div className="flex items-center gap-4 bg-zinc-800/30 p-3 rounded-2xl border border-zinc-700/30">
                      <div className="w-10 h-10 shrink-0 bg-zinc-800 rounded-xl flex items-center justify-center text-blue-400 border border-zinc-700/50">
                        <AppleShareIcon className="w-5 h-5" />
                      </div>
                      <span className="text-sm text-zinc-300 font-medium">1. Tap the Share icon</span>
                    </div>
                    <div className="flex items-center gap-4 bg-zinc-800/30 p-3 rounded-2xl border border-zinc-700/30">
                      <div className="w-10 h-10 shrink-0 bg-zinc-800 rounded-xl flex items-center justify-center text-white border border-zinc-700/50">
                        <AppleAddIcon className="w-5 h-5" />
                      </div>
                      <span className="text-sm text-zinc-300 font-medium">2. Select Add to Home Screen</span>
                    </div>
                  </div>
                </>
              )}

              {promptType === 'ios-other' && (
                <>
                  <h3 className="text-xl font-bold text-white mb-2">Use Safari</h3>
                  <p className="text-sm text-zinc-400 mb-6">
                    To install AirChips, please open this page in the <span className="text-white font-semibold">Safari</span> browser.
                  </p>
                </>
              )}

              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => handleDismiss(true)}
                  className="flex-1 py-3 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-300 text-xs font-semibold rounded-xl transition-all"
                >
                  Don't show again
                </button>
                <button
                  onClick={() => handleDismiss(false)}
                  className="flex-1 py-3 bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-semibold rounded-xl transition-all"
                >
                  Remind me later
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
